import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { getClaim } from '../../services/suiteApi';

function fmtMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '--';
  return `$${Number(n).toFixed(2)}`;
}

function Section({ title, children, action }) {
  return (
    <section className="dm-card">
      <div className="dm-card__title">
        <span>{title}</span>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function ClaimDetailPage() {
  const { claimId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  usePageTitle(data?.ExternalId || data?.ClaimNumber || `Claim ${claimId?.slice(0, 8)}`);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const resp = await getClaim(claimId);
        if (!cancelled) setData(resp?.data || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [claimId]);

  const lines = data?.Lines || data?.lines || data?.LineItems || [];
  const remits = data?.Remits || data?.remits || data?.Remittances || [];
  const denials = data?.DenialEvents || data?.denialEvents || [];

  return (
    <div className="dm-page">
      <header className="dm-page__header">
        <div className="dm-page__crumbs">
          <Link to="/claims">Claims</Link> <span>·</span>{' '}
          <span>{data?.ExternalId || claimId?.slice(0, 8)}</span>
        </div>
        <h1 className="dm-page__title">
          {data?.ExternalId || data?.ClaimNumber || `Claim ${claimId?.slice(0, 8)}`}
        </h1>
        {data?.PayerName && (
          <p className="dm-page__lede">{data.PayerName}</p>
        )}
        <div className="dm-page__actions">
          <Link to="/ClaimRight" className="dm-btn dm-btn--secondary">Run scrubber</Link>
          <Link to="/AuthEligibility" className="dm-btn dm-btn--secondary">Check eligibility</Link>
          <Link to="/AppealBuilder" className="dm-btn dm-btn--primary">File appeal</Link>
        </div>
      </header>

      {error && <div className="dm-banner dm-banner--error">{error}</div>}
      {loading && <div className="dm-card__empty">Loading claim...</div>}

      {data && (
        <>
          <Section title="Header">
            <dl className="dm-keyvals">
              <div><dt>Status</dt><dd>{data.Status || data.status || '--'}</dd></div>
              <div><dt>Claim number</dt><dd>{data.ClaimNumber || '--'}</dd></div>
              <div><dt>Patient</dt><dd>{data.PatientId || '--'}</dd></div>
              <div><dt>Service date</dt><dd>{data.ServiceDate || data.serviceDate || '--'}</dd></div>
              <div><dt>Total charge</dt><dd>{fmtMoney(data.TotalCharge || data.totalCharge)}</dd></div>
              <div><dt>Total paid</dt><dd>{fmtMoney(data.TotalPaid || data.totalPaid)}</dd></div>
              <div><dt>Balance</dt><dd>{fmtMoney(data.Balance || data.balance)}</dd></div>
            </dl>
          </Section>

          <Section title={`Lines (${lines.length})`}>
            {lines.length === 0 ? (
              <div className="dm-card__empty">No line items on file.</div>
            ) : (
              <table className="dm-table">
                <thead>
                  <tr>
                    <th>CPT</th>
                    <th>Modifiers</th>
                    <th>Units</th>
                    <th className="dm-table__num">Charge</th>
                    <th className="dm-table__num">Allowed</th>
                    <th className="dm-table__num">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={l.LineId || l.id || i}>
                      <td>{l.CptCode || l.cpt || l.code || '--'}</td>
                      <td>{(l.Modifiers || l.modifiers || []).join(' ')}</td>
                      <td>{l.Units || l.units || 1}</td>
                      <td className="dm-table__num">{fmtMoney(l.Charge || l.charge)}</td>
                      <td className="dm-table__num">{fmtMoney(l.Allowed || l.allowed)}</td>
                      <td className="dm-table__num">{fmtMoney(l.Paid || l.paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section title={`Remittance events (${remits.length})`}>
            {remits.length === 0 ? (
              <div className="dm-card__empty">No remits posted yet.</div>
            ) : (
              <ul className="dm-list">
                {remits.map((r, i) => (
                  <li key={r.RemitId || i} className="dm-list__row">
                    <div className="dm-list__primary">
                      {r.PaidAmount != null ? fmtMoney(r.PaidAmount) : 'remit'}
                    </div>
                    <div className="dm-list__secondary">{r.CheckNumber || r.checkNumber || ''}</div>
                    <div className="dm-list__meta">{r.PostedAt || r.postedAt || ''}</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {denials.length > 0 && (
            <Section title={`Denial events (${denials.length})`}>
              <ul className="dm-list">
                {denials.map((d, i) => (
                  <li key={i} className="dm-list__row">
                    <div className="dm-list__primary">
                      {(d.CarcCodes || d.carcCodes || []).join(', ') || 'denial'}
                    </div>
                    <div className="dm-list__secondary">{d.Reason || d.reason || ''}</div>
                    <div className="dm-list__meta">{d.PostedAt || d.postedAt || ''}</div>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
